import { useState, useRef, useEffect } from "react";
import { Eye, EyeOff, Info, Upload, X, Loader2, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Checkbox,
} from "@b2b/ui-components";
import { Textarea } from "../../../shadcn/ui/Textarea";

import { ProfileField } from "../types/profile";
import { cn } from "../../../lib/utils";
import { CountryPicker } from "../../../components/CountryPicker";

const FIELD_LABEL_CLASS = "text-sm font-medium text-gray-700";

function isPdfUrl(url: string): boolean {
  return /\.pdf$/i.test((url.split("?")[0] || "").toLowerCase());
}

function isImageUrl(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test((url.split("?")[0] || "").toLowerCase());
}

interface ProfileFieldRendererProps {
  field: ProfileField;
  fieldKey: string;
  isEditing: boolean;
  onValueChange: (key: string, value: string | File | boolean | string[]) => void;
  error?: string;
  required?: boolean;
  /** If provided, files are uploaded to S3 and the returned URL is stored instead of the raw File */
  onUpload?: (file: File) => Promise<string>;
  /** Max file size in bytes (e.g. 2 * 1024 * 1024 for 2MB). When set, uploads over this size are rejected. */
  maxFileSizeBytes?: number;
  /**
   * Buyer profile_settings only: adds padding on the country dropdown’s search row so the
   * placeholder is not clipped beside the search icon.
   */
  padBuyerProfileCountrySearchField?: boolean;
  /**
   * Buyer profile_settings: show the red required asterisk only while the section is in edit mode
   * (hides * on read-only / view presentation for non-editable required fields).
   */
  hideRequiredAsteriskUnlessEditing?: boolean;
}

export const ProfileFieldRenderer = ({
  field,
  fieldKey,
  isEditing,
  onValueChange,
  error,
  required,
  onUpload,
  maxFileSizeBytes,
  padBuyerProfileCountrySearchField,
  hideRequiredAsteriskUnlessEditing,
}: ProfileFieldRendererProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    typeof field.value === "string" ? field.value : undefined,
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const multiInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (field.type !== "upload") return;
    setPreviewUrl(typeof field.value === "string" ? field.value : undefined);
  }, [field.type, field.value]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setUploadError(null);
    if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
      setUploadError("File size must be 2MB or less.");
      event.target.value = "";
      return;
    }
    if (onUpload) {
      setIsUploading(true);
      try {
        const url = await onUpload(file);
        setPreviewUrl(url);
        onValueChange(fieldKey, url);
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Upload failed. Please try again.";
        setUploadError(message);
        setPreviewUrl(undefined);
      } finally {
        setIsUploading(false);
      }
    } else {
      if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
        setUploadError("File size must be 2MB or less.");
        event.target.value = "";
        return;
      }
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      onValueChange(fieldKey, file);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setPreviewUrl("");
    setUploadError(null);
    onValueChange(fieldKey, "");
  };

  if (field.type === "readonly") {
    return (
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {field.label}
        </Label>
        <p className="text-sm font-medium text-gray-900">
          {Array.isArray(field.value)
            ? (field.value as string[]).join(", ")
            : typeof field.value === "object" && field.value !== null
              ? JSON.stringify(field.value)
              : String(field.value ?? "Not specified")}
        </p>
      </div>
    );
  }

  if (field.type === "button") {
    return (
      <div className="space-y-2">
        <Label className={FIELD_LABEL_CLASS}>{field.label}</Label>
        <Button
          variant={field.variant || "default"}
          size={field.size || "default"}
          className="w-full"
          onClick={() => field.href && window.open(field.href, "_blank")}
        >
          {typeof field.value === "string"
            ? field.value
            : Array.isArray(field.value)
              ? (field.value as string[]).join(", ")
              : String(field.value ?? "")}
        </Button>
      </div>
    );
  }

  if (!isEditing && field.editable) {
    return (
      <div className="space-y-2">
        <Label className={FIELD_LABEL_CLASS}>{field.label}</Label>
        <div className="w-full min-h-10 px-3 py-2 rounded border border-gray-300 bg-gray-50 text-sm text-gray-700 flex flex-col justify-center">
          {field.type === "upload" && previewUrl ? (
            isImageUrl(previewUrl) ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="h-20 w-20 object-cover rounded-md"
              />
            ) : isPdfUrl(previewUrl) ? (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-20 w-28 flex-col items-center justify-center gap-1 rounded-md border border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-50"
              >
                <FileText className="h-8 w-8" />
                <span className="text-[10px]">PDF document</span>
              </a>
            ) : (
              <span className="text-sm text-gray-700 truncate max-w-full">
                {previewUrl.split("/").pop() || "File uploaded"}
              </span>
            )
          ) : field.type === "password" ? (
            "••••••••"
          ) : field.type === "switch" ? (
            field.value === true ||
            field.value === "true" ||
            field.value === "Active" ? (
              "Active"
            ) : (
              "Inactive"
            )
          ) : field.type === "radio" && field.options ? (
            (field.options.find(
              (o) =>
                o.value ===
                (typeof field.value === "string" ? field.value : ""),
            )?.label ??
            (typeof field.value === "string" ? field.value : "Not set"))
          ) : field.type === "tags" ? (
            typeof field.value === "string" && field.value ? (
              field.value
            ) : (
              "Not set"
            )
          ) : field.type === "checkboxGroup" ? (
            Array.isArray(field.value) && field.value.length > 0 ? (
              (field.value as string[]).join(", ")
            ) : (
              "Not set"
            )
          ) : field.type === "uploadMulti" ? (
            Array.isArray(field.value) && field.value.length > 0 ? (
              `${(field.value as string[]).length} file(s)`
            ) : (
              "Not set"
            )
          ) : (
            Array.isArray(field.value) || (typeof field.value === "object" && field.value !== null)
              ? "—"
              : String(field.value ?? "Not set")
          )}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-2"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {field.type !== "switch" && (
        <Label className={`${FIELD_LABEL_CLASS} flex items-center gap-1`}>
          {field.label}
          {(field.required || required) &&
            (!hideRequiredAsteriskUnlessEditing || isEditing) && (
              <span className="text-red-600">*</span>
            )}
        </Label>
      )}

      {field.type === "text" && fieldKey === "description" && (
        <Textarea
          value={
            typeof field.value === "string"
              ? field.value
              : String(field.value || "")
          }
          onChange={(e) => onValueChange(fieldKey, e.target.value)}
          placeholder={
            field.placeholder || `Enter ${field.label.toLowerCase()}`
          }
          rows={6}
          className={cn(
            "min-h-[140px] resize-y transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary/20",
            error && "border-destructive focus-visible:ring-destructive/20",
          )}
          disabled={!field.editable}
        />
      )}

      {field.type === "text" && fieldKey !== "description" && (
        <div className="space-y-1.5">
          <Input
            value={
              typeof field.value === "string"
                ? field.value
                : String(field.value || "")
            }
            onChange={(e) => onValueChange(fieldKey, e.target.value)}
            placeholder={
              field.placeholder || `Enter ${field.label.toLowerCase()}`
            }
            className={cn(
              "h-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20",
              error && "border-destructive focus:ring-destructive/20",
              !field.editable && "cursor-not-allowed bg-muted/60 text-foreground",
            )}
            disabled={!field.editable}
          />
          {field.readOnlyHint ? (
            <p className="flex gap-2 text-xs text-muted-foreground leading-snug">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" aria-hidden />
              <span>{field.readOnlyHint}</span>
            </p>
          ) : null}
        </div>
      )}

      {field.type === "number" && (
        <Input
          type="number"
          value={
            typeof field.value === "string"
              ? field.value
              : String(field.value || "")
          }
          onChange={(e) => onValueChange(fieldKey, e.target.value)}
          placeholder={
            field.placeholder || `Enter ${field.label.toLowerCase()}`
          }
          className={cn(
            "h-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20",
            error && "border-destructive focus:ring-destructive/20",
          )}
          disabled={!field.editable}
        />
      )}

      {field.type === "password" && (
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            value={
              typeof field.value === "string"
                ? field.value
                : String(field.value || "")
            }
            onChange={(e) => onValueChange(fieldKey, e.target.value)}
            placeholder={field.placeholder || "Enter password"}
            className={cn(
              "h-10 pr-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20",
              error && "border-destructive focus:ring-destructive/20",
            )}
            disabled={!field.editable}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}

      {field.type === "select" && (fieldKey === "country" || fieldKey === "companyCountry") && (
        <CountryPicker
          value={typeof field.value === "string" ? field.value : String(field.value ?? "")}
          onValueChange={(value) => onValueChange(fieldKey, value)}
          placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
          disabled={!field.editable}
          triggerClassName={cn(
            "transition-all duration-200 focus:ring-2 focus:ring-primary/20",
            error && "border-destructive focus:ring-destructive/20",
          )}
          commandInputWrapperClassName={
            padBuyerProfileCountrySearchField
              ? "gap-3 px-3.5 min-w-0"
              : undefined
          }
          commandInputClassName={
            padBuyerProfileCountrySearchField
              ? "h-8 min-h-0 py-0 pl-2 pr-2 flex-1 min-w-0 text-left self-center"
              : undefined
          }
        />
      )}

      {field.type === "select" && fieldKey !== "country" && fieldKey !== "companyCountry" && (
        <Select
          value={
            typeof field.value === "string"
              ? field.value
              : field.value !== undefined && field.value !== null
                ? String(field.value)
                : undefined
          }
          onValueChange={(value) => onValueChange(fieldKey, value)}
          disabled={!field.editable}
        >
          <SelectTrigger
            className={cn(
              "h-10 w-full transition-all duration-200 focus:ring-2 focus:ring-primary/20 bg-white border-gray-300",
              error && "border-destructive focus:ring-destructive/20",
            )}
          >
            <SelectValue
              placeholder={
                field.placeholder || `Select ${field.label.toLowerCase()}`
              }
            />
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-300 shadow-md z-50">
            {field.options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {field.type === "switch" && (
        <div className="space-y-2">
          <Label className={FIELD_LABEL_CLASS}>{field.label}</Label>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm text-muted-foreground">
                Enable or disable user account
              </p>
            </div>
            <Switch
              checked={
                field.value === true ||
                field.value === "true" ||
                field.value === "Active"
              }
              onCheckedChange={(checked) => onValueChange(fieldKey, checked)}
              disabled={!field.editable}
            />
          </div>
        </div>
      )}

      {field.type === "radio" && (
        <div className="space-y-3">
          {field.options?.map((option) => (
            <label
              key={option.value}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                typeof field.value === "string" && field.value === option.value
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/30",
              )}
            >
              <input
                type="radio"
                name={fieldKey}
                value={option.value}
                checked={
                  typeof field.value === "string" &&
                  field.value === option.value
                }
                onChange={() => onValueChange(fieldKey, option.value)}
                disabled={!field.editable}
                className="mt-1 h-4 w-4 border-gray-300 text-primary focus:ring-primary"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {option.label}
                </p>
                {option.value && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {option.value}
                  </p>
                )}
              </div>
            </label>
          ))}
        </div>
      )}

      {field.type === "tags" && (
        <div className="space-y-2">
          <Input
            value={
              typeof field.value === "string"
                ? field.value
                : String(field.value || "")
            }
            onChange={(e) => onValueChange(fieldKey, e.target.value)}
            placeholder={
              field.placeholder ||
              "Enter values separated by comma or press Enter to add"
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const current =
                  typeof field.value === "string" ? field.value : "";
                const trimmed = (e.target as HTMLInputElement).value?.trim();
                if (trimmed) {
                  const next = current ? `${current}, ${trimmed}` : trimmed;
                  onValueChange(fieldKey, next);
                  (e.target as HTMLInputElement).value = "";
                }
              }
            }}
            className={cn(
              "h-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20",
              error && "border-destructive focus:ring-destructive/20",
            )}
            disabled={!field.editable}
          />
          {field.placeholder && (
            <p className="text-xs text-muted-foreground">{field.placeholder}</p>
          )}
        </div>
      )}

      {field.type === "upload" && (
        <div className="space-y-3">
          {isUploading ? (
            <div className="flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg border-primary/40 bg-primary/5">
              <div className="flex flex-col items-center gap-2 text-primary">
                <Loader2 className="w-8 h-8 animate-spin" />
                <p className="text-sm font-medium">Uploading…</p>
              </div>
            </div>
          ) : previewUrl ? (
            <div className="relative inline-block">
              {isImageUrl(previewUrl) ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="h-32 w-32 object-cover rounded-lg border-2 border-border"
                />
              ) : isPdfUrl(previewUrl) ? (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-32 w-36 flex-col items-center justify-center gap-2 rounded-lg border-2 border-border bg-muted/40 text-muted-foreground hover:bg-muted/60"
                >
                  <FileText className="h-10 w-10" />
                  <span className="text-xs font-medium">PDF document</span>
                </a>
              ) : (
                <span className="text-sm text-muted-foreground px-2">
                  {previewUrl.split("/").pop() || "File uploaded"}
                </span>
              )}
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute -top-2 -right-2 h-6 w-6"
                onClick={removeFile}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <label
                className={cn(
                  "flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200",
                  "hover:bg-accent/20 border-border hover:border-primary/50",
                  (error || uploadError) && "border-destructive",
                )}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-3 text-muted-foreground" />
                  <p className="mb-2 text-sm text-muted-foreground">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, PDF (MAX. {maxFileSizeBytes === 2 * 1024 * 1024 ? "2MB" : "5MB"})
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  disabled={!field.editable}
                />
              </label>
            </div>
          )}
          {uploadError && (
            <p className="text-sm text-destructive">{uploadError}</p>
          )}
        </div>
      )}

      {field.type === "checkboxGroup" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-4">
            {field.options?.map((option) => {
              const arr = (Array.isArray(field.value) ? field.value : []) as string[];
              const checked = arr.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(checked) => {
                      const next: string[] = checked
                        ? [...arr, option.value]
                        : arr.filter((v) => v !== option.value);
                      onValueChange(fieldKey, next);
                    }}
                    disabled={!field.editable}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {field.type === "uploadMulti" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {Array.isArray(field.value)
              ? (field.value as string[]).map((url, idx) => (
                  <div
                    key={idx}
                    className="relative inline-block border rounded-lg overflow-hidden"
                  >
                    {/\.(jpg|jpeg|png|gif|webp)$/i.test(String(url)) ? (
                      <img
                        src={String(url)}
                        alt=""
                        className="h-20 w-20 object-cover"
                      />
                    ) : (
                      <div className="h-20 w-20 flex items-center justify-center bg-muted text-xs text-center px-1">
                        {String(url).split("/").pop()?.slice(0, 12) || "File"}
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-0 right-0 h-5 w-5"
                      onClick={() => {
                        const arr = field.value as string[];
                        const next = arr.filter((_, i) => i !== idx);
                        onValueChange(fieldKey, next);
                      }}
                      disabled={!field.editable}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))
              : null}
          </div>
          {isUploading && (
            <div className="flex items-center gap-2 text-primary text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading…
            </div>
          )}
          {uploadError && (
            <p className="text-sm text-destructive">{uploadError}</p>
          )}
          <label
            className={cn(
              "flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors",
              "hover:bg-accent/20 border-border hover:border-primary/50",
              (!field.editable || isUploading) && "pointer-events-none opacity-60",
            )}
          >
            <div className="flex flex-col items-center justify-center py-3">
              <Upload className="w-6 h-6 mb-1 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Click to add file (PNG, JPG, PDF)</p>
            </div>
            <input
              ref={multiInputRef}
              type="file"
              className="hidden"
              accept="image/*,.pdf"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadError(null);
                if (onUpload) {
                  setIsUploading(true);
                  try {
                    const url = await onUpload(file);
                    const arr = Array.isArray(field.value) ? (field.value as string[]) : [];
                    onValueChange(fieldKey, [...arr, url]);
                  } catch {
                    setUploadError("Upload failed. Please try again.");
                  } finally {
                    setIsUploading(false);
                  }
                } else {
                  onValueChange(fieldKey, file);
                }
                if (multiInputRef.current) multiInputRef.current.value = "";
              }}
              disabled={!field.editable || isUploading}
            />
          </label>
        </div>
      )}

      {error && (
        <motion.p
          className="text-sm text-destructive"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
};
